import React, { useContext, useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Google, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import notification from '../utility/notification.js';
import logo from "../Assets/images/ConnectHealthPro.png";
import { post, get } from '../utility/fetch.js';
import { UserContext } from '../contexts/UserContext.js';
import { requestNotificationPermission, signInWithGooglePopup, getGoogleRedirectResult } from '../utility/firebase.js';
import { getFinanceAuthToken } from '../utility/financeAuth.js';
import axios from 'axios';
import { GoogleAuthProvider } from 'firebase/auth';


const StyledPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  borderRadius: 0,
  flexDirection: 'row',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  flex: 0.7,
  display: 'flex',
  position: 'relative',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 0.3,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  backgroundColor: '#3C7E2D',
  color: '#fff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    flex: 1,
  },
}));

const SocialButton = styled(Button)(({ theme }) => ({
  width: '100%',
  justifyContent: 'center',
  textTransform: 'none',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'white',
  color: '#3C3C3E',
  border: '1px solid #ddd',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(2),
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  color: '#fff',
  textAlign: 'center',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
}));

const SubLogoText = styled(Typography)(({ theme }) => ({
  color: '#fff',
  textAlign: 'center',
  fontSize: '1rem',
  opacity: 0.9,
  marginBottom: theme.spacing(4),
}));

export default function SignInPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loginStep, setLoginStep] = useState(1); // 1: Email/Phone, 2: Password
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [userInfo, setUserInfo] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const Navigate = useNavigate();
  const { setUserId, setProfilePicture } = useContext(UserContext);

  // Check for redirect result on mount
  React.useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getGoogleRedirectResult();
        if (result?.user) {
          await handleGoogleSignIn(result);
        }
      } catch (error) {
        console.error('Error checking redirect result:', error);
      }
    };
    checkRedirectResult();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Step 1: Check if user exists by email or phone
  const handleEmailSubmit = async (e) => {
    e?.preventDefault();

    if (!formData.username.trim()) {
      notification({
        message: "Please enter your email or phone number",
        type: "error"
      });
      return;
    }

    setCheckingEmail(true);
    try {
      const response = await get(`/employee/employee-by-email-or-phone?emailOrPhone=${formData.username}`);

      if (response?.statusCode === 200 && response?.data) {
        // User exists, proceed to password step
        setUserInfo(response.data);
        setLoginStep(2);
      } else {
        // User doesn't exist
        notification({
          message: "No account found with this email or phone number. Please sign up first.",
          type: "error"
        });
      }
    } catch (error) {
      console.error('Email/Phone verification error:', error);
      notification({
        message: "No account found with this email or phone number. Please sign up first.",
        type: "error"
      });
    } finally {
      setCheckingEmail(false);
    }
  };

  // Go back to email/phone step
  const handleBackToEmail = () => {
    setLoginStep(1);
    setFormData({ ...formData, password: '' });
    setUserInfo(null);
  };

  const getEmployeeData = async (userId) => {
    try {
      const response = await get(`/employee/employee/${userId}`);
      if (response?.statusCode === 200) {
        // Store profile picture URL in session storage
        setProfilePicture(response?.data?.profilePicture)
        sessionStorage.setItem('profilePicture', response.data.profilePicture);
        // Store other useful data
        sessionStorage.setItem('email', response.data.email);
        sessionStorage.setItem('title', response.data.title);
        sessionStorage.setItem('gender', response.data.gender);
        sessionStorage.setItem('phoneNumber', response.data.phoneNumber);
        sessionStorage.setItem('department', response.data.department);

        // Store healthcare provider info if needed
        if (response.data.healthCareProvider) {
          sessionStorage.setItem('providerName', response.data.healthCareProvider.name);
          sessionStorage.setItem('providerLocation', response.data.healthCareProvider.location);
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to fetch employee data:', error);
      notification({
        type: 'error',
        message: 'Failed to fetch employee data'
      });
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loginStep === 1) {
      handleEmailSubmit();
    } else {
      signin();
    }
  };

  const signin = async () => {
    if (!formData.password) {
      notification({
        type: "error",
        message: "Please enter your password"
      });
      return;
    }

    setLoading(true);
    try {
      let res = await post("/auth/login", { email: formData.username, password: formData.password });

      if (res.statusCode === 200 || res?.status === "success") {
        // Store auth data (adjust based on your actual response structure)
        const userData = res.data || res;

        sessionStorage.setItem('token', userData.token);
        sessionStorage.setItem('name', userData.firstName ?
          userData.firstName + ' ' + userData.lastName :
          userData.name);
        sessionStorage.setItem('userId', userData.userId || userData.userID);
        sessionStorage.setItem('clinicId', res?.data.clinicId);

        // Store completion fields
        if (userData.isAssociationCompleted !== undefined) {
          sessionStorage.setItem('isAssociationCompleted', userData.isAssociationCompleted);
        }
        if (userData.isAwardsCompleted !== undefined) {
          sessionStorage.setItem('isAwardsCompleted', userData.isAwardsCompleted);
        }
        if (userData.isCertificateCompleted !== undefined) {
          sessionStorage.setItem('isCertificateCompleted', userData.isCertificateCompleted);
        }
        if (userData.isEducationCompleted !== undefined) {
          sessionStorage.setItem('isEducationCompleted', userData.isEducationCompleted);
        }
        if (userData.isLicenceCompleted !== undefined) {
          sessionStorage.setItem('isLicenceCompleted', userData.isLicenceCompleted);
        }
        if (userData.isWorkExperienceCompleted !== undefined) {
          sessionStorage.setItem('isWorkExperienceCompleted', userData.isWorkExperienceCompleted);
        }

        // Request Firebase device token
        try {
          console.log("Requesting Firebase device token...");
          const deviceToken = await requestNotificationPermission();
          if (deviceToken) {
            console.log("Device token obtained:", deviceToken);
            // Send device token to the backend
            await post("/auth/update-device-token", {
              userId: parseInt(res.data.userId),
              deviceToken: deviceToken,
            });
            await post(`/UserPresence/connect/${res.data.userId}`, {
              userId: res.data.userId,
            });
            console.log("Device token sent successfully");
          } else {
            console.warn("No device token available");
          }
        } catch (error) {
          console.error("Failed to send device token:", error);
        }

        // Get finance authentication token
        try {
          const financeToken = await getFinanceAuthToken(
            userData.userId || userData.userID,
            userData.healthCareProviderId
          );

          if (financeToken) {
            sessionStorage.setItem('financeToken', financeToken);
            console.log('Finance token stored successfully');
          } else {
            console.warn('Failed to obtain finance token');
          }
        } catch (financeError) {
          console.error('Finance authentication failed:', financeError);
          // Don't block login if finance auth fails
        }

        // Set context
        setUserId(userData.userId || userData.userID);

        if (userData.profilePicture) {
          setProfilePicture(userData.profilePicture);
          sessionStorage.setItem('profilePicture', userData.profilePicture);
        }

        // Store other data if available
        if (userData.healthCareProviderId) {
          sessionStorage.setItem('healthCareProviderId', userData.healthCareProviderId);
        }
        if (userData.roles && userData.roles[0]) {
          sessionStorage.setItem('role', userData.roles[0].name);
        }
        if (userData.userName) {
          sessionStorage.setItem('userName', userData.userName);
        }

        notification({
          message: "Login successful",
          type: "success"
        });
        Navigate('/landing');

        // Fetch additional employee data in background (non-blocking)
        getEmployeeData(userData.userId || userData.userID).catch(error => {
          console.error('Background employee data fetch failed:', error);
        });

      } else {
        notification({
          message: 'Invalid credentials. Please check your password.',
          type: "error"
        });
      }
    } catch (error) {
      console.error('Login failed:', error);
      notification({
        type: 'error',
        message: 'Login failed. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }

  const handleGoogleSignIn = async (result) => {
    try {
      const user = result.user;
      const firebaseIdToken = await user.getIdToken();
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const googleIdToken = credential.idToken;

      // Send to backend for authentication using axios
      const baseUrl = process.env.REACT_APP_BACKEND_URL_WEB;
      const res = await axios.post(`${baseUrl}/Google/google-login`, {
        idToken: googleIdToken,
      });

      console.log('Google login response:', res.data);

      if (res.data.code === 1) {
        const userData = res.data.data;

        // Store auth data
        sessionStorage.setItem('token', userData.token);
        sessionStorage.setItem('refreshToken', userData.refreshToken);
        sessionStorage.setItem('name', userData.name || user.displayName);
        sessionStorage.setItem('userId', userData.userID);
        sessionStorage.setItem('healthCareProviderId', userData.healthCareProviderId);
        sessionStorage.setItem('role', userData.roles?.[0]?.name ?? '');
        sessionStorage.setItem('userName', userData.userName);

        // Store social role privileges
        if (userData.socialRole?.socialRolePrivileges) {
          sessionStorage.setItem('privileges', JSON.stringify(userData.socialRole.socialRolePrivileges));
          const privilegeNames = userData.socialRole.socialRolePrivileges
            .filter(p => p.status === 1)
            .map(p => p.socialPrivilege.name);
          sessionStorage.setItem('privilegeNames', JSON.stringify(privilegeNames));
        }

        // Store completion fields
        if (userData.isAssociationCompleted !== undefined) {
          sessionStorage.setItem('isAssociationCompleted', userData.isAssociationCompleted);
        }
        if (userData.isAwardsCompleted !== undefined) {
          sessionStorage.setItem('isAwardsCompleted', userData.isAwardsCompleted);
        }
        if (userData.isCertificateCompleted !== undefined) {
          sessionStorage.setItem('isCertificateCompleted', userData.isCertificateCompleted);
        }
        if (userData.isEducationCompleted !== undefined) {
          sessionStorage.setItem('isEducationCompleted', userData.isEducationCompleted);
        }
        if (userData.isLicenceCompleted !== undefined) {
          sessionStorage.setItem('isLicenceCompleted', userData.isLicenceCompleted);
        }
        if (userData.isWorkExperienceCompleted !== undefined) {
          sessionStorage.setItem('isWorkExperienceCompleted', userData.isWorkExperienceCompleted);
        }

        // Request Firebase device token
        try {
          const deviceToken = await requestNotificationPermission();
          if (deviceToken) {
            await post("/Auth/update-device-token", {
              userId: userData.userID,
              deviceToken: deviceToken,
            });
            await post(`/UserPresence/connect/${userData.userID}`, {
              userId: userData.userID,
            });
          }
        } catch (error) {
          console.error("Failed to send device token:", error);
        }

        // Get finance authentication token
        try {
          const financeToken = await getFinanceAuthToken(
            userData.userID,
            userData.healthCareProviderId
          );
          if (financeToken) {
            sessionStorage.setItem('financeToken', financeToken);
          }
        } catch (financeError) {
          console.error('Finance authentication failed:', financeError);
        }

        // Set context
        setUserId(userData.userID);
        if (userData.profilePicture || user.photoURL) {
          setProfilePicture(userData.profilePicture || user.photoURL);
          sessionStorage.setItem('profilePicture', userData.profilePicture || user.photoURL);
        }

        // Fetch additional employee data
        const employeeDataFetched = await getEmployeeData(userData.userID);

        if (employeeDataFetched) {
          notification({
            message: "Google Sign-In successful",
            type: "success"
          });
          Navigate('/landing');
        } else {
          notification({
            type: 'error',
            message: 'Failed to fetch complete profile data'
          });
        }
      } else {
        notification({
          message: res.data.message || 'Google Sign-In failed. Please try again.',
          type: "error"
        });
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      notification({
        type: 'error',
        message: 'Google Sign-In failed. Please try again.'
      });
    }
  };

  const handleGoogleButtonClick = async () => {
    setGoogleLoading(true);
    try {
      const result = await signInWithGooglePopup();
      await handleGoogleSignIn(result);
    } catch (error) {
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        notification({
          type: 'warning',
          message: 'Popup was blocked. Please allow popups for this site.'
        });
      } else if (error.code !== 'auth/cancelled-popup-request') {
        notification({
          type: 'error',
          message: 'Google Sign-In failed. Please try again.'
        });
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <StyledPaper>
      <ImageSection>
        <div>
          <img
            style={{ width: '50vw', height: '50vh', objectFit: 'cover' }}
            src="/loginpic.png" alt="Healthcare"
          />
        </div>
      </ImageSection>
      <FormSection>
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
          <LogoText variant="h4">ConnectHealthPro</LogoText>
          {/* Step 1: Email/Phone Input */}
          {loginStep === 1 && (
            <>
              <Typography variant="h6" sx={{ color: 'white', mb: 2, textAlign: 'center' }}>
                Sign In to Your Account
              </Typography>

              <TextField
                fullWidth
                name="username"
                label="Email Address"
                variant="outlined"
                margin="normal"
                value={formData.username}
                onChange={handleChange}
                onKeyPress={(e) => e.key === 'Enter' && handleEmailSubmit()}
                disabled={checkingEmail}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  marginBottom: 2,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                    '&:hover fieldset': { borderColor: '#3C7E2D' },
                    '&.Mui-focused fieldset': { borderColor: '#3C7E2D' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#3C7E2D',
                    fontWeight: 500,
                    backgroundColor: 'white',
                    padding: '0 4px',
                    borderRadius: '4px',
                    '&.Mui-focused': {
                      color: '#3C7E2D',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleEmailSubmit}
                disabled={checkingEmail}
                sx={{ mb: 3 }}
              >
                {checkingEmail ? 'Verifying...' : 'Continue'}
              </Button>

              {/* <Divider sx={{ mb: 3, color: 'white' }}>OR</Divider>

              <Stack spacing={2}>
                <SocialButton
                  startIcon={<Google />}
                  onClick={handleGoogleButtonClick}
                  disabled={googleLoading || loading}
                >
                  {googleLoading ? 'Signing in with Google...' : 'Login with Google'}
                </SocialButton>
              </Stack> */}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Don't have an account?{' '}
                  <Button
                    onClick={() => Navigate('/create-account')}
                    sx={{
                      color: '#D9FEAA',
                      textDecoration: 'underline',
                      '&:hover': { color: '#fff' }
                    }}
                  >
                    Sign Up
                  </Button>
                </Typography>
              </Box>
            </>
          )}

          {/* Step 2: Password Input */}
          {loginStep === 2 && (
            <>
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToEmail}
                sx={{
                  color: 'white',
                  mb: 2,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Change Email
              </Button>

              <Typography variant="h6" sx={{ color: 'white', mb: 1, textAlign: 'center' }}>
                Welcome Back!
              </Typography>
              <Typography variant="body2" sx={{ color: '#D9FEAA', mb: 3, textAlign: 'center' }}>
                {formData.username}
              </Typography>

              <TextField
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                value={formData.password}
                onChange={handleChange}
                onKeyPress={(e) => e.key === 'Enter' && signin()}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          color: '#3C7E2D',
                          '&:hover': {
                            backgroundColor: 'rgba(60, 126, 45, 0.04)',
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  marginBottom: 2,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                    '&:hover fieldset': { borderColor: '#3C7E2D' },
                    '&.Mui-focused fieldset': { borderColor: '#3C7E2D' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#3C7E2D',
                    fontWeight: 500,
                    backgroundColor: 'white',
                    padding: '0 4px',
                    borderRadius: '4px',
                    '&.Mui-focused': {
                      color: '#3C7E2D',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                  },
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button onClick={() => Navigate('/forgot-password')} sx={{ color: 'white' }}>
                  Forgot Password?
                </Button>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                size="large"
                onClick={signin}
                disabled={loading}
                sx={{ mb: 3 }}
              >
                {loading ? 'Logging In...' : 'Log In'}
              </Button>
            </>
          )}
        </Box>
      </FormSection>
    </StyledPaper>
  );
}